import React, { useState } from 'react';
import { format } from 'date-fns';
import axios from '../../services/axios';
import toast from 'react-hot-toast';

const statusColors = {
    draft: 'bg-gray-100 text-gray-800',
    submitted: 'bg-yellow-100 text-yellow-800',
    verified: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    returned: 'bg-red-100 text-red-800',
    rejected: 'bg-red-200 text-red-900'
};

const statusLabels = {
    draft: 'Draft',
    submitted: 'Submitted',
    verified: 'Verified',
    approved: 'Approved',
    returned: 'Needs Update',
    rejected: 'Rejected'
};

const HourEntryList = ({ entries, onUpdate, onEdit }) => {
    const [actionLoading, setActionLoading] = useState(null);

    if (!entries || entries.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500">
                No hour entries yet. Start logging your hours!
            </div>
        );
    }

    const handleAction = async (entryId, action) => {
        setActionLoading(entryId);
        try {
            await axios.post(`/hours/${entryId}/${action}`);
            toast.success(`Entry ${action}ed successfully`);
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.message || `Failed to ${action} entry`);
        } finally {
            setActionLoading(null);
        }
    };

    const handleDelete = async (entryId) => {
        if (!window.confirm('Are you sure you want to delete this entry?')) return;

        setActionLoading(entryId);
        try {
            await axios.delete(`/hours/${entryId}`);
            toast.success('Entry deleted successfully');
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete entry');
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <div className="space-y-4">
            {entries.map((entry) => (
                <div key={entry.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                            <div className="flex items-center space-x-2">
                                <h3 className="font-medium text-gray-900 capitalize">
                                    {entry.activity_type?.replace(/_/g, ' ') || 'Other'}
                                </h3>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[entry.status] || 'bg-gray-100 text-gray-800'}`}>
                                    {statusLabels[entry.status] || entry.status}
                                </span>
                            </div>

                            <p className="text-sm text-gray-600 mt-1">
                                {entry.description || 'No description provided'}
                            </p>

                            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                                <span>{format(new Date(entry.date), 'MMM d, yyyy')}</span>
                                <span>•</span>
                                <span>{entry.HourCategory?.display_name || 'Uncategorized'}</span>
                                <span>•</span>
                                <span className="font-medium text-gray-900">{entry.hours} hours</span>
                                {entry.evidence_url && (
                                    <>
                                        <span>•</span>
                                        <a href={entry.evidence_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                            View Evidence
                                        </a>
                                    </>
                                )}
                                {entry.mentor_comments && (
                                    <>
                                        <span>•</span>
                                        <span className="text-gray-600">💬 {entry.mentor_comments}</span>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center space-x-2 flex-wrap gap-2">
                            {entry.status === 'draft' && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => onEdit?.(entry)}
                                        className="px-3 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-50 min-h-[44px]"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleAction(entry.id, 'submit')}
                                        disabled={actionLoading === entry.id}
                                        className="px-3 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 min-h-[44px]"
                                    >
                                        Submit
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(entry.id)}
                                        disabled={actionLoading === entry.id}
                                        className="px-3 py-2 border border-red-200 text-red-700 text-sm rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 min-h-[44px]"
                                    >
                                        Delete
                                    </button>
                                </>
                            )}
                            {entry.status === 'submitted' && (
                                <span className="text-xs text-gray-600">Submitted — awaiting mentor review</span>
                            )}
                            {entry.status === 'verified' && (
                                <span className="text-xs text-gray-600">Verified — awaiting approval</span>
                            )}
                            {entry.status === 'returned' && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => onEdit?.(entry)}
                                        className="px-3 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-50 min-h-[44px]"
                                    >
                                        Update
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleAction(entry.id, 'submit')}
                                        disabled={actionLoading === entry.id}
                                        className="px-3 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 min-h-[44px]"
                                    >
                                        Resubmit
                                    </button>
                                </>
                            )}
                            {entry.status === 'approved' && (
                                <span className="text-xs font-medium text-gray-900">Approved — counts toward completion</span>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default HourEntryList;
